"use client";

import { Button } from "@/components/themes/ui/button";
import { addtowishlist } from "@/clicks/wishlistClicks";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import SvgIcons from "@/components/themes/SvgIcons";
import { getAssetsBaseUrl, getCurrencySymbol } from "@/services/storeService";

interface CartItemCardProps {
  item: any;
  itemidex: number;
  productwishlistData: any[];
  setProductWishlistData: (data: any[]) => void;
  handleQuantityChange: (product: any, newQuantity: number, index: number) => void;
  handleRemoveCartChange: (product: any, index: number) => void;
  count: number;
  setCount: (count: number) => void;
  navigate: (path: string) => void;
}

export default function CartItemCard({
  item,
  itemidex,
  productwishlistData,
  setProductWishlistData,
  handleQuantityChange,
  handleRemoveCartChange,
  navigate,
}: CartItemCardProps) {
  const assets = `${getAssetsBaseUrl()}`
  const router = useRouter();

  // ---------- IMAGE HELPERS ----------
  const getCartImageUrl = (imageurl: string | string[] | undefined): string => {
    if (!imageurl) return "/ecomsaas-assets/images/no-image.webp";

    const urls = Array.isArray(imageurl)
      ? imageurl
      : imageurl.split(",").map((s) => s.trim()).filter(Boolean);

    const VIDEO_EXT = /\.(mp4|webm|mov|avi|wmv|mkv|flv|ogv)$/i;
    const firstImage = urls.find((url) => !VIDEO_EXT.test(url));

    return firstImage ? `${assets}${firstImage}` : "/ecomsaas-assets/images/no-image.webp";
  };

  // ---------- WISHLIST ----------
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);

  const handleWishlistClick = () => {
    setIsHeartAnimating(true);
    addtowishlist(item, productwishlistData, setProductWishlistData, router.push);
    setTimeout(() => setIsHeartAnimating(false), 500);
  };



  // ---------- RENDER ----------
  return (
    <div
      className="border-b border-[#D7D7D7] last:border-b-0"
    >
      <div className="flex w-full space-x-2 my-4">
        {/* ---- IMAGE ---- */}
        <div
          className="rounded-lg p-1 w-[100px] h-[100px] cursor-pointer"
          onClick={() => navigate(`/p/${item.seo_url}`)}
        >
          <img
            className="rounded w-full h-full object-top object-cover"
            src={getCartImageUrl(item.imageurl)}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/ecomsaas-assets/images/no-image.webp";
            }}
            alt={item.name}
            loading="lazy"
          />
        </div>

        {/* ---- DETAILS ---- */}
        <div className="flex-1">
          <p
            className="md:text-lg text-sm font-semibold cursor-pointer"
            onClick={() => navigate(`/p/${item.seo_url}`)}
          >
            {item.name}
          </p>

          {/* ---- PRICE ---- */}
          <div className="flex items-center gap-2 mt-1">
            <span className="font-bold">{getCurrencySymbol()} {(parseFloat(item.sp)).toFixed(2)}</span>

            {parseFloat(item.sp) !== parseFloat(item.mrp) ? (
              <>
                <span className="md:text-[14px] text-[12px] line-through text-gray-500">
                  {getCurrencySymbol()} {parseFloat(item.mrp || 0).toFixed(2)}
                </span>
                <span className="md:text-[14px] text-[10px] text-offer_text">
                  You saved {getCurrencySymbol()}
                  {(parseFloat(item.mrp || 0) - parseFloat(item.sp || 0)).toFixed(2)}!
                </span>
              </>
            ) : (
              <span className="md:text-[14px] text-[10px] text-offer_text">
                Exclusive best price!
              </span>
            )}
          </div>

          {/* ---- VARIANTS ---- */}
          <div className="text-sm mt-1 space-y-1">
            {item.selectedSize?.code && (
              <div>
                <strong>Size:</strong> {item.selectedSize.code}
              </div>
            )}
            {item.selectedWeight?.code && (
              <div>
                <strong>Weight:</strong> {item.selectedWeight.code}
              </div>
            )}
            {item["Custom Text"] && (
              <div>
                <strong>Custom Text:</strong> {item["Custom Text"]}
              </div>
            )}
            {item["Custom Text1"] && (
              <div>
                <strong>Custom Text 2:</strong> {item["Custom Text1"]}
              </div>
            )}
            {item["Men Size"] && (
              <div>
                <strong>Couple Size:</strong> {item["Couple Size"]}
              </div>
            )}
          </div>

          <div className="flex justify-between mt-[10px] ">
            <div className="flex items-center">
              <Button
                disabled={parseInt(item?.cartquantity) <= 1}
                onClick={() =>
                  handleQuantityChange(item, item?.cartquantity - 1, itemidex)
                }
                className="border-2 border-border_color p-[14px]"
              >
                -
              </Button>
              <div>
                {/* <p className="!w-[30px] !text-center">{item?.cartQuantity}</p> */}
                <p className="!w-[30px] !text-center">
                  {parseInt(item?.cartquantity) || 1}
                </p>
              </div>
              <Button
                style={{ background: "none" }}
                disabled={parseInt(item?.cartquantity) >= 10}
                onClick={() =>
                  handleQuantityChange(item, item?.cartquantity + 1, itemidex)
                }
                className="border-2 border-border_color p-[14px]"
              >
                +
              </Button>
            </div>

            {/* Wishlist + Remove */}
            <div className="flex items-center gap-2">
              {/* Wishlist */}
              <button
                onClick={handleWishlistClick}
                className={`p-2 rounded-lg `}
                style={{ background: "#F0F0F0" }}
              >
                <svg
                  className={`w-6 h-6 rounded-lg transition-all ${isHeartAnimating ? "animate-heart" : ""
                    }`}
                  viewBox="0 0 27 27"
                  fill={productwishlistData.includes(item.id.toString()) ? "#f45440" : "none"}
                  stroke={productwishlistData.includes(item.id.toString()) ? "#f45440" : "#000"}
                  strokeWidth="1.5"
                >
                  <path d="M13.4993 26.2061L4.70067 16.9253C3.9281 16.1443 3.41815 15.1374 3.24307 14.0471C3.06798 12.9568 3.23664 11.8385 3.72514 10.8505V10.8505C4.09415 10.1046 4.63318 9.45803 5.29779 8.96406C5.96241 8.47008 6.73359 8.14284 7.54782 8.00931C8.36204 7.87578 9.19599 7.93978 9.98095 8.19603C10.7659 8.45228 11.4794 8.89345 12.0627 9.48319L13.4993 10.9358L14.9359 9.48319C15.5192 8.89345 16.2327 8.45228 17.0177 8.19603C17.8026 7.93978 18.6366 7.87578 19.4508 8.00931C20.265 8.14284 21.0362 8.47008 21.7008 8.96406C22.3654 9.45803 22.9045 10.1046 23.2735 10.8505V10.8505C23.762 11.8385 23.9306 12.9568 23.7556 14.0471C23.5805 15.1374 23.0705 16.1443 22.298 16.9253L13.4993 26.2061Z" />
                </svg>
              </button>

              {/* Remove */}
              <button
                onClick={() => handleRemoveCartChange(item, itemidex)}
                className="p-2 rounded-lg"
                style={{ background: "#F0F0F0" }}
              >
                <SvgIcons.CartDeleteIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---- HEART ANIMATION ---- */}
      <style jsx>{`
        @keyframes heartAnimation {
          0% { transform: scale(1); }
          20% { transform: scale(1.3); }
          40% { transform: scale(1.1) rotate(-5deg); }
          60% { transform: scale(1.2) rotate(5deg); }
          80% { transform: scale(1.1) rotate(-3deg); }
          100% { transform: scale(1); }
        }
        .animate-heart {
          animation: heartAnimation 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}